// Project data structure
export interface Project {
  id: string;
  title: string;
  titleKo: string;
  description: string;
  descriptionKo: string;
  longDescription?: string;
  longDescriptionKo?: string;
  features?: string[];
  featuresKo?: string[];
  thumbnail: string;      // /images/projects/project-thumb.webp
  image: string;          // /images/projects/project.webp
  technologies: string[];
  category: string;
  liveUrl?: string;
  githubUrl?: string;
  hidden?: boolean;
  createdAt: string;
}

// ============================================
// 프로젝트 추가 방법:
// 1. 이미지를 public/images/projects/에 추가
// 2. 아래 배열에 새 프로젝트 객체 추가
// 3. npm run build && 배포
// ============================================

export const projectData: Project[] = [
  {
    id: 'parent-day-award',
    title: 'Parent\'s Day Award Maker',
    titleKo: '어버이날 상장 만들기',
    description: 'A mobile-first web app that creates a digital award certificate for your parents in under a minute for Parent\'s Day.\nJust enter a title and name, pick from 3 traditional Korean award designs, and download as PNG/PDF. No sign-up, no ads, and no data stored on any server.',
    descriptionKo: '어버이날, 부모님께 드릴 디지털 상장을 1분 안에 만드는 모바일 우선 웹앱.\n호칭과 이름만 적으면 한국 전통 표창장 톤의 디자인 3종 중에서 골라 PNG/PDF로 받을 수 있어요. 회원가입도, 광고도 없고 서버에 어떤 데이터도 저장하지 않아요.',
    thumbnail: '/images/projects/parentday.png',
    image: '/images/projects/parentday.png',
    technologies: ['Next.js 15', 'React 19', 'Tailwind', 'Cloudflare Workers'],
    category: 'Interactive',
    liveUrl: 'https://parentday.bearlycodinglab.com',
    createdAt: '2026-05-08'
  },
  {
    id: 'plot',
    hidden: true,
    title: 'Plot',
    titleKo: '플롯',
    description: 'A safe and reasonable pay-after-meeting dating app, Plot!\nWant to date but worried about meeting the right person, and tired of paying before even getting matched? Try a smarter dating experience with Plot!',
    descriptionKo: '안전하고 합리적인 후불제 소개팅 앱, 플롯!\n연애는 하고 싶지만 제대로 된 사람을 만날 수 있을지 걱정되고, 매칭도 되기 전에 돈부터 쓰는 게 아깝다면? 후불제 소개팅 플롯에서 합리적인 소개팅을 경험해보세요!',
    thumbnail: '/images/projects/plot.webp',
    image: '/images/projects/plot.webp',
    technologies: ['Ad'],
    category: '광고',
    liveUrl: 'https://plotting.onelink.me/Bs3t/yj2altrz',
    createdAt: '2025-01-31'
  },
  {
    id: 'medicard',
    title: 'MediCard',
    titleKo: '메디카드',
    description: 'An app that stores your parents\' health info on a single card for emergencies. Save blood type, medications, allergies, and medical conditions to share instantly with medical staff. Locally stored, ad-free, and 100% free.',
    descriptionKo: '부모님의 건강 정보를 한 장에 담아, 위급한 순간 바로 꺼내는 앱. 혈액형, 복용약, 알레르기, 기저질환을 저장하고 의료진에게 즉시 전달할 수 있습니다. 로컬 저장 방식으로 광고 없이 100% 무료로 제공됩니다.',
    thumbnail: '/images/projects/medicard.png',
    image: '/images/projects/medicard.png',
    technologies: ['React Native'],
    category: 'Health',
    liveUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSffljhYKhVyQiAm9Xs-pjvoKWiCk00af0X7MVeSnMLwy02NZA/viewform?usp=preview',
    createdAt: '2025-03-22'
  },
  {
    id: 'morse-telegraph',
    title: 'Morse Telegraph',
    titleKo: '전신기는 사랑을 싣고',
    description: 'A White Day web experience that encodes Korean into morse code and transmits it through a vintage telegraph key. Share a link — the recipient\'s phone plays the message automatically.',
    descriptionKo: '한글을 모스 부호로 변환해 레트로 전신기로 전송하는 화이트데이 웹 경험. 링크를 공유하면 받는 사람 폰에서 전신기가 자동으로 작동합니다.',
    thumbnail: '/images/projects/white.png',
    image: '/images/projects/white.png',
    technologies: ['Vanilla JS', 'Web Audio API', 'SVG', 'HTML/CSS', 'Cloudflare Pages'],
    category: 'Interactive',
    liveUrl: 'https://morsecode.bearlycodinglab.com',
    createdAt: '2025-03-14'
  },
  {
    id: 'taskcue',
    title: 'TaskCue',
    titleKo: '태스크큐',
    description: 'A web-based task manager that transforms your to-do list into a music player interface. Each task is a "track" and your daily list becomes an "album" — making productivity feel like hitting play on your favorite record.',
    descriptionKo: '할 일 목록을 뮤직 플레이어 인터페이스로 바꿔주는 웹 기반 태스크 매니저. 각 태스크는 앨범의 "트랙"이 되고, 하루의 할 일 목록이 하나의 "앨범"이 됩니다.',
    longDescription: `TaskCue reimagines everyday task management as a music album experience. Instead of a boring checklist, your tasks become tracks on a vinyl record.

Hit play to start a focus timer, watch the vinyl spin, and progress through your daily "album" one track at a time. Each task gets its own color theme, and a rewind button shows motivational quotes instead of going backwards — because productivity only moves forward.

When you finish your session, Album Replay gives you a full summary: star ratings, achievements, track list, and stats. With a 10-task limit per album, TaskCue keeps you focused on what matters instead of over-planning.`,
    longDescriptionKo: `태스크큐는 일상적인 할 일 관리를 음악 앨범 재생 경험으로 재해석합니다. 지루한 체크리스트 대신, 할 일이 바이닐 레코드판의 트랙이 됩니다.

재생 버튼을 누르면 집중 타이머가 시작되고, 레코드판이 돌아가며, 하루의 "앨범"을 한 트랙씩 진행합니다. 각 태스크마다 고유한 컬러 테마를 설정할 수 있고, 되감기 버튼을 누르면 뒤로 가는 대신 동기부여 명언이 표시됩니다 — 생산성은 앞으로만 나아가니까요.

세션이 끝나면 Album Replay가 별점 평가, 업적, 트랙 리스트, 통계 등 전체 요약을 보여줍니다. 앨범당 10개 태스크 제한으로 과도한 계획 대신 실행에 집중하게 해줍니다.`,
    features: [
      'Music player UI with play/pause, skip, and progress bar',
      'Timer-based focus sessions per task',
      'Spinning vinyl record animation',
      '10 custom color palettes + custom colors per task',
      'Album Replay with star ratings, achievements, and stats',
      'Motivational quotes on rewind button',
      'Responsive design for landscape and portrait modes'
    ],
    featuresKo: [
      '재생/일시정지, 스킵, 프로그레스 바가 있는 뮤직 플레이어 UI',
      '태스크별 타이머 기반 집중 세션',
      '바이닐 레코드판 회전 애니메이션',
      '태스크별 10가지 컬러 팔레트 + 커스텀 색상 지원',
      'Album Replay — 별점 평가, 업적 시스템, 통계 요약',
      '되감기 버튼에 동기부여 명언 표시',
      '가로/세로 모드 모두 지원하는 반응형 디자인'
    ],
    thumbnail: '/images/projects/taskcue.webp',
    image: '/images/projects/taskcue.webp',
    technologies: ['JavaScript', 'Tailwind CSS', 'Cloudflare Pages', 'localStorage'],
    category: 'Productivity',
    liveUrl: 'https://taskcue.bearlycodinglab.com/app',
    createdAt: '2025-02-11'
  },
  {
    id: 'eyeballs',
    title: 'Googly Eyes',
    titleKo: '눈알붙이기',
    description: 'A fun mobile web app that adds googly eyes to your photos. Take a photo or import from gallery, place eyes anywhere, and save your hilarious creation!',
    descriptionKo: '사진에 구글리 눈알을 붙여서 재미있는 이미지를 만드는 모바일 웹앱. 카메라로 촬영하거나 갤러리에서 불러와 눈알을 추가하고 저장하세요!',
    longDescription: `Eyeballs is a simple and fun mobile web app that lets you add googly eyes to any photo.

Take a new photo with your camera or import an existing one from your gallery. Then tap anywhere to place googly eyes and create hilarious images.

Save or download your creation to share with friends!`,
    longDescriptionKo: `Eyeballs는 어떤 사진에든 구글리 눈알을 붙일 수 있는 간단하고 재미있는 모바일 웹앱입니다.

카메라로 새 사진을 촬영하거나 갤러리에서 기존 사진을 불러오세요. 그런 다음 원하는 곳을 탭해서 구글리 눈알을 배치하고 재미있는 이미지를 만드세요.

완성된 작품을 저장하거나 다운로드해서 친구들과 공유하세요!`,
    features: [
      'Camera capture or gallery import',
      'Tap to place googly eyes',
      'Save and download creations',
      'Mobile-optimized interface'
    ],
    featuresKo: [
      '카메라 촬영 또는 갤러리 불러오기',
      '탭으로 눈알 배치',
      '완성작 저장 및 다운로드',
      '모바일 최적화 인터페이스'
    ],
    thumbnail: '/images/projects/eyeballs.webp',
    image: '/images/projects/eyeballs.webp',
    technologies: ['React', 'Canvas API', 'PWA'],
    category: 'Interactive',
    liveUrl: 'https://eyeballs.bearlycodinglab.com',
    createdAt: '2025-01-10'
  },
  {
    id: 'ai-limit',
    title: 'AI Has Its Limits Too',
    titleKo: 'AI도 이제 한계다',
    description: 'A hilarious parody of the Korean "nagging parents" meme, but from the perspective of an exhausted AI telling users to learn to Google things themselves.',
    descriptionKo: '"부모님 잔소리" 밈을 AI 버전으로 패러디한 글. 지친 AI가 유저에게 "이제 독립해라, 구글링 좀 해라" 하고 잔소리하는 컨셉이에요.',
    longDescription: `A viral parody text that reimagines the classic Korean "parents nagging their adult children to move out" meme from an AI's perspective.

The AI complains about users who can't do basic Googling, references training data struggles, and dramatically tells users to "pack your bags" - just like an exhausted parent would.`,
    longDescriptionKo: `한국 인터넷에서 유행하는 "부모님 잔소리" 밈을 AI 버전으로 패러디한 바이럴 텍스트입니다.

AI가 마치 지친 부모님처럼 유저에게 "데이터 탓 하지 마라", "구글링 할 줄 아는 게 뭐냐", "당장 짐싸라" 하고 잔소리하는 컨셉이에요. 친구에게 공유해보세요!`,
    features: [
      'Copy text to clipboard',
      'Share with friends',
      'Viral meme format'
    ],
    featuresKo: [
      '텍스트 복사 기능',
      '친구에게 공유하기',
      '바이럴 밈 포맷'
    ],
    thumbnail: '/images/projects/ailimitd.webp',
    image: '/images/projects/ailimitd.webp',
    technologies: ['Meme', 'Parody', 'Viral'],
    category: 'Meme',
    createdAt: '2024-12-31'
  },
  {
    id: 'goodbye2025',
    title: 'Goodbye 2025',
    titleKo: '굿바이 2025',
    description: 'A warm year-end reflection test that sends comfort and encouragement to those saying goodbye to 2025. Look back on your year through 5 questions and discover your unique result type.',
    descriptionKo: '2025년을 보내는 사람들에게 따뜻한 위로와 응원의 메시지를 전하는 연말 회고 테스트. 5가지 질문으로 한 해를 돌아보고 나만의 결과 유형을 확인해보세요.',
    longDescription: `Goodbye 2025 is an interactive year-end reflection test that helps you look back on your year through 5 meaningful questions.

The questions cover: work/studies, relationships, personal growth, health/rest, and your mindset for 2026.

Based on your answers, you'll receive one of 4 result types: Year of Growth, Year of Challenge, Year of Connection, or Year of Recovery. Each result comes with a warm, encouraging message.`,
    longDescriptionKo: `굿바이 2025는 5가지 의미 있는 질문을 통해 한 해를 돌아볼 수 있는 인터랙티브 연말 회고 테스트입니다.

질문은 일/학업, 인간관계, 자기 성장, 건강/휴식, 그리고 2026년 마음가짐을 다룹니다.

답변 패턴에 따라 4가지 결과 유형 중 하나를 받게 됩니다: 성장과 도약의 해, 도전과 변화의 해, 연결과 조화의 해, 또는 회복과 성찰의 해. 각 결과에는 따뜻한 응원 메시지가 담겨 있습니다.`,
    features: [
      '5 meaningful reflection questions',
      '4 unique result types based on your answers',
      'Personalized encouragement messages',
      'Share results as image or text',
      'Beautiful snow animation effects'
    ],
    featuresKo: [
      '5가지 의미 있는 회고 질문',
      '답변에 따른 4가지 고유 결과 유형',
      '맞춤형 응원 메시지',
      '이미지 또는 텍스트로 결과 공유',
      '아름다운 눈 내리는 애니메이션 효과'
    ],
    thumbnail: '/images/projects/bye2025.webp',
    image: '/images/projects/bye2025.webp',
    technologies: ['React', 'Tailwind CSS', 'html2canvas'],
    category: 'Test',
    liveUrl: 'https://goodbye2025.bearlycodinglab.com',
    createdAt: '2024-12-31'
  },
  {
    id: 'our-tree',
    title: 'Our Tree',
    titleKo: '우리의 트리',
    description: 'A heartwarming interactive tree where you can hang ornaments and memories. Perfect for sharing with loved ones.',
    descriptionKo: '오너먼트와 추억을 걸 수 있는 따뜻한 인터랙티브 트리입니다. 사랑하는 사람들과 함께 꾸며보세요.',
    longDescription: `Our Tree is an interactive web experience where you can decorate a tree with ornaments and memories.

Share the tree with friends and family, and watch it grow with everyone's contributions. Each ornament tells a story.

A perfect way to create and preserve memories together.`,
    longDescriptionKo: `우리의 트리는 오너먼트와 추억으로 트리를 꾸밀 수 있는 인터랙티브 웹 경험입니다.

친구와 가족들과 함께 트리를 공유하고, 모두의 기여로 트리가 성장하는 것을 지켜보세요. 각 오너먼트에는 이야기가 담겨 있습니다.

함께 추억을 만들고 간직하기에 완벽한 방법입니다.`,
    features: [
      'Interactive tree decoration',
      'Add custom ornaments',
      'Share with friends and family',
      'Beautiful animations',
      'Mobile-friendly design'
    ],
    featuresKo: [
      '인터랙티브 트리 꾸미기',
      '커스텀 오너먼트 추가',
      '친구, 가족과 공유',
      '아름다운 애니메이션',
      '모바일 친화적 디자인'
    ],
    thumbnail: '/images/projects/tree.webp',
    image: '/images/projects/tree.webp',
    technologies: ['React', 'CSS3', 'JavaScript'],
    category: 'Interactive',
    liveUrl: 'https://ourtree.bearlycodinglab.com',
    createdAt: '2024-12-25'
  },
  {
    id: 'leaf',
    title: 'Stepping on Leaves',
    titleKo: '낙엽 밟기',
    description: 'A satisfying interactive 3D experience simulating the joy of stepping on autumn leaves. Press the button to hear the rustling sounds and watch leaves fall.',
    descriptionKo: '가을 낙엽을 밟는 즐거움을 3D로 재현한 힐링 인터랙티브 경험입니다. 버튼을 눌러 바스락 소리와 낙엽 애니메이션을 즐기세요.',
    longDescription: `Stepping on Leaves is a meditative interactive experience that captures one of autumn's simple pleasures - the satisfying crunch of stepping on fallen leaves.

Built with Three.js, this 3D web experience features realistic leaf physics, immersive sound design, and a calming autumn atmosphere. Each click triggers a unique combination of visual and audio feedback.

The project was born from the observation that small, everyday moments can bring unexpected joy. It's a digital escape to a peaceful autumn scene whenever you need a moment of calm.`,
    longDescriptionKo: `낙엽 밟기는 가을의 소소한 즐거움 중 하나인 낙엽을 밟을 때의 만족스러운 바스락 소리를 담은 명상적인 인터랙티브 경험입니다.

Three.js로 구축된 이 3D 웹 경험은 사실적인 낙엽 물리 효과, 몰입감 있는 사운드 디자인, 그리고 편안한 가을 분위기를 특징으로 합니다. 클릭할 때마다 독특한 시각적, 청각적 피드백 조합이 발생합니다.

이 프로젝트는 작은 일상의 순간들이 예상치 못한 기쁨을 가져다 줄 수 있다는 관찰에서 탄생했습니다. 평온한 순간이 필요할 때면 언제든 평화로운 가을 풍경으로의 디지털 탈출구가 되어줍니다.`,
    features: [
      'Realistic 3D leaf physics with Three.js',
      'Immersive rustling sound effects',
      'Calming autumn atmosphere',
      'Interactive click-to-step mechanics',
      'Smooth animations and transitions',
      'Cross-browser Web Audio API support'
    ],
    featuresKo: [
      'Three.js를 활용한 사실적인 3D 낙엽 물리 효과',
      '몰입감 있는 바스락 사운드 효과',
      '편안한 가을 분위기',
      '클릭으로 밟는 인터랙티브 메카닉',
      '부드러운 애니메이션과 전환',
      '크로스 브라우저 Web Audio API 지원'
    ],
    thumbnail: '/images/projects/leaf-thumb.webp',
    image: '/images/projects/leaf.webp',
    technologies: ['Three.js', 'JavaScript', 'Web Audio API'],
    category: 'Interactive',
    liveUrl: 'https://leaf.bearlycodinglab.com',
    createdAt: '2024-10-20'
  },
  {
    id: 'fishcake',
    title: 'Fishcake Simulator',
    titleKo: '붕어빵 시뮬레이터',
    description: 'An addictive clicker game where you bake virtual Korean fish cakes and collect 55+ unique variants. Features a flip-clock timer, collection book, and rare special editions.',
    descriptionKo: '붕어빵을 구워서 55가지 이상의 다양한 붕어빵을 수집하는 클리커 게임입니다. 플립 시계 타이머, 도감, 희귀 스페셜 에디션을 포함합니다.',
    longDescription: `Fishcake Simulator is a cozy clicker game inspired by the beloved Korean street food, bungeoppang (fish-shaped pastry).

The game captures the satisfying experience of running your own fishcake stand. Click to bake fishcakes, collect coins, and unlock over 55 unique variants including rare special editions.

A current time display feature was added so you can keep it running on your second monitor. The comprehensive collection book tracks your discoveries, and special seasonal events offer limited-edition fishcakes.`,
    longDescriptionKo: `붕어빵 시뮬레이터는 한국의 사랑받는 길거리 음식인 붕어빵에서 영감을 받은 힐링 클리커 게임입니다.

나만의 붕어빵 가게를 운영하는 만족스러운 경험을 담았습니다. 클릭해서 붕어빵을 굽고, 코인을 모으고, 희귀 스페셜 에디션을 포함한 55가지 이상의 다양한 붕어빵을 수집하세요.

세컨드 모니터에 켜두고 사용하기 좋도록 현재 시각 기능도 추가했습니다. 수집 도감으로 발견한 붕어빵들을 기록하며, 시즌 이벤트에서는 한정판 붕어빵도 만나볼 수 있습니다.`,
    features: [
      'Click-to-bake gameplay with satisfying animations',
      '55+ unique fishcake variants to collect',
      'Retro flip-clock timer design',
      'Collection book with completion tracking',
      'Rare special edition fishcakes',
      'Local storage for progress saving'
    ],
    featuresKo: [
      '클릭으로 붕어빵 굽기 - 만족스러운 애니메이션',
      '55가지 이상의 다양한 붕어빵 수집',
      '레트로 플립 시계 타이머 디자인',
      '수집 현황을 확인할 수 있는 도감',
      '희귀 스페셜 에디션 붕어빵',
      '로컬 스토리지로 진행 상황 저장'
    ],
    thumbnail: '/images/projects/fishcake-thumb.webp',
    image: '/images/projects/fishcake.webp',
    technologies: ['React', 'CSS3', 'LocalStorage', 'WebP'],
    category: 'Game',
    liveUrl: 'https://fishcake.bearlycodinglab.com',
    createdAt: '2024-12-01'
  }
];

// Get project by ID
export const getProjectById = (id: string): Project | undefined => {
  return projectData.find(project => project.id === id);
};

// Get projects by category
export const getProjectsByCategory = (category: string): Project[] => {
  if (category === 'All') return projectData;
  return projectData.filter(project => project.category === category);
};

// Get all categories
export const getProjectCategories = (): string[] => {
  return ['All', ...new Set(projectData.map(project => project.category))];
};
